// Copyright (c) StrangeBee 2023

import { ReactElement, ReactNode, memo } from 'react';
import { AttachmentType } from 'thehive-sdk';
import { classList } from '@Helpers/className';
import { ListActionType } from '@Types';

import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { GenericAttachmentType } from '@Molecules/THPreview';
import { CommentAttachment } from './CommentAttachment';
import { CommentDate } from './CommentDate';

import './Comment.scss';

interface CommentProps {
    date: number;
    actions?: Record<string, ListActionType>;
    attachments?: AttachmentType[];
    avatar?: string;
    className?: string;
    content: string | ReactNode;
    edited?: number;
    editedPrefix?: string;
    isChatComment?: boolean;
    isCurrentUser?: boolean;
    isEditing?: boolean;
    name?: string;
    onTogglePreview?: (attachment: GenericAttachmentType) => void;
    parentId?: string;
}

export const Comment = memo(
    ({
        date,
        actions,
        attachments,
        avatar = '',
        className,
        content,
        edited,
        editedPrefix,
        isChatComment = false,
        isCurrentUser = false,
        isEditing = false,
        name = '',
        onTogglePreview,
        parentId,
    }: CommentProps): ReactElement => (
        <article className={classList(['Comment', { 'Comment--current': isCurrentUser }, className])}>
            <aside className="Comment__Aside">
                <UserAvatar className="Comment__Avatar" avatar={avatar} size="small" name={name} showTooltip />
                {actions && <ActionsList actionList={actions} isIcon scope="both" />}
            </aside>

            <main className="Comment__Main">
                <div
                    className={classList(
                        'Comment__Content',
                        `Comment__Content--padding${isChatComment ? 'Small' : 'Normal'}`,
                        { 'Comment__Content--isEditing': isEditing },
                    )}
                >
                    {typeof content !== 'string' ? content : <p className="Comment__Text">{content}</p>}

                    {!!attachments?.length && (
                        <ul className="Comment__Attachments">
                            {attachments.map((attachment) => (
                                <CommentAttachment
                                    key={attachment._id}
                                    attachment={attachment}
                                    parentId={parentId}
                                    togglePreview={onTogglePreview}
                                />
                            ))}
                        </ul>
                    )}
                </div>

                {!isEditing && (
                    <div className="Comment__Date">
                        <CommentDate date={date} />
                        {!!edited && editedPrefix && <CommentDate date={edited} prefix={editedPrefix} />}
                    </div>
                )}
            </main>
        </article>
    ),
);
